import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { Trip, TripsResponse } from '../types/trip';

type Props = {
  limit?: number;
  showViewAllLink?: boolean;
  title?: string;
};

export default function TripsSection({ limit = 5, showViewAllLink = true, title = 'Recommended Trips' }: Props) {
  const [trips, setTrips] = useState<Trip[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        params.set('limit', String(limit));
        
        console.log('🟢 [Frontend] Fetching trips:', `/api/trips?${params}`);
        
        const response = await fetch(`/api/trips?${params}`, {
          signal: controller.signal,
        });
        
        console.log('🟢 [Frontend] Response status:', response.status);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('🔴 [Frontend] Request failed:', response.status, errorText);
          throw new Error(`Request failed with status ${response.status}`);
        }
        const data: TripsResponse = await response.json();
        
        console.log('🟢 [Frontend] Received data:', {
          tripsCount: data.trips?.length ?? 0,
          total: data.total,
          success: data.success
        });
        
        setTrips(data.trips ?? []);
      } catch (err: any) {
        if (err.name !== 'AbortError') {
          console.error('🔴 [Frontend] Failed to load trips:', err);
          setError('Unable to load trips. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => controller.abort();
  }, [limit]);

  if (loading) {
    return (
      <div className="flex gap-4 overflow-x-auto pb-4">
        {Array.from({ length: limit }).map((_, i) => (
          <div key={i} className="shrink-0 w-56 h-64 bg-gray-200 animate-pulse rounded" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <p className="text-center text-sm text-red-600">{error}</p>
    );
  }

  if (!trips.length) {
    return (
      <p className="text-center text-sm text-gray-500">No trips found.</p>
    );
  }

  return (
    <>
      <div className="flex gap-4 overflow-x-auto pb-4">
        {trips.map((trip) => {
          const imageUrl = trip.cover_image_url || trip.image_url;
          return (
            <div 
              key={trip.id}
              onClick={() => navigate(`/trip/${trip.id}`)}
              className="shrink-0 w-56 border border-gray-200 hover:border-black transition-colors group cursor-pointer"
            >
              {imageUrl ? (
                <div className="h-56 overflow-hidden bg-gray-100">
                  <img 
                    src={imageUrl} 
                    alt={trip.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                  />
                </div>
              ) : (
                <div className="h-56 bg-gray-200 flex items-center justify-center">
                  <span className="text-4xl font-bold text-gray-400">{trip.title.charAt(0)}</span>
                </div>
              )}
              <div className="p-4">
                <h3 className="font-semibold text-sm truncate">{trip.title}</h3>
                {trip.description && (
                  <p className="text-xs text-gray-500 mt-1 truncate">{trip.description}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
      
      {showViewAllLink && (
        <Link 
          to="/recommended/trips"
          className="inline-block mt-6 bg-black text-waybook-green px-6 py-3 font-semibold hover:bg-gray-900 transition-colors rounded-lg"
        >
          View All Trips
        </Link>
      )}
    </>
  );
}
